import { ApolloClient, ApolloLink, from, InMemoryCache, NormalizedCacheObject } from '@apollo/client'
import { BatchHttpLink } from '@apollo/client/link/batch-http'
import { setContext } from '@apollo/client/link/context'
import { Mutex } from 'async-mutex'
import forEach from 'lodash/forEach'
import merge from 'lodash/merge'
import { GetServerSidePropsContext } from 'next'
import getConfig from 'next/config'
import { Cookies } from 'react-cookie'

import fragments from '@Generated/fragments'

if (typeof globalThis !== 'undefined') {
  // TODO: remove this before migrating to v4
  globalThis[Symbol.for('apollo.deprecations')] = true
}

let apolloClient: ApolloClient<NormalizedCacheObject>

let requestCount = 0
const mutexes = new Map<string, Mutex>()
const operationToMutexName = new Map([
  ['AddToCart', 'cart'],
  ['AuthenticateCart', 'cart'],
  ['Cart', 'cart'],
  ['CartSummary', 'cart'],
  ['CheckoutCart', 'cart'],
  ['FinalizeCart', 'cart'],
  ['RefreshCart', 'cart'],
  ['RemoveFromCart', 'cart'],
  ['SetAddressesInCart', 'cart'],
  ['SetDiscountCodeInCart', 'cart'],
  ['SetQuantityInCart', 'cart'],
  ['SetShippingMethodInCartDelivery', 'cart'],
  ['SetVariantInCart', 'cart'],
])

export interface Config {
  forwardSetCookie?: (setCookie: string) => void
  headers?: Record<string, string>
  locale?: string
  preview?: boolean
  previewOverrideCurrentDate?: string
}

const createApolloClient = (config: Config = {}): ApolloClient<NormalizedCacheObject> => {
  const {
    publicRuntimeConfig: {
      graphqlEndpoint: graphqlEndpointClient,
      graphqlEndpointDomainProxyExtra,
    } = { graphqlEndpoint: '', graphqlEndpointDomainProxyExtra: '' },
    serverRuntimeConfig: {
      graphqlEndpoint: graphqlEndpointServer,
    } = { graphqlEndpoint: '' },
  } = getConfig()

  const graphqlEndpoint = typeof window === 'undefined' ? graphqlEndpointServer : graphqlEndpointClient

  const links: ApolloLink[] = []

  if (typeof window !== 'undefined') {
    links.push(setContext(async (_, context) => {
      if (context.withAuth) {
        const cookies = new Cookies()
        let accessToken = cookies.get('th_aat')

        if (!accessToken) {
          const res = await fetch(
            '/v1/auth/customers/refresh',
            {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
              },
              method: 'POST',
            },
          )
          const data = await res.json() as { status: string }

          if (data?.status !== 'success') {
            return Promise.reject(new Error('refresh failed'))
          }

          accessToken = cookies.get('th_aat')
          if (!accessToken) {
            return Promise.reject(new Error('no access token after refresh'))
          }
        }

        return {
          headers: {
            ...context.headers,
            Authorization: `Bearer ${accessToken}`,
          },
        }
      }

      return context
    }))
  }

  if (typeof window === 'undefined' && config.forwardSetCookie) {
    links.push(new ApolloLink((operation, forward) => forward(operation).map((response) => {
      const res: Response | undefined = operation.getContext()?.response
      if (!res) {
        return response
      }

      // @ts-ignore
      res.headers?.getSetCookie?.().forEach(setCookie => config.forwardSetCookie?.(setCookie))

      return response
    })))
  }

  links.push(new BatchHttpLink({
    headers: config.headers,
    credentials: 'include',
    fetch: async (input: RequestInfo, init?: RequestInit) => {
      let requestInit = init

      if (typeof window !== 'undefined' && window.thSentry && requestInit) {
        const traceData = window.thSentry.getTraceData()
        if (traceData['sentry-trace']) {
          requestInit = {
            ...requestInit,
            headers: {
              ...requestInit.headers as Record<string, string>,
              'sentry-trace': traceData['sentry-trace'],
              ...traceData.baggage ? { baggage: traceData.baggage } : {},
            },
          }
        }
      }

      let mutexName: string | undefined

      try {
        const url = typeof input === 'string' ? input : input.url
        mutexName = new URL(url).searchParams.get('mu') || undefined
      } catch (e) {
        // ignore errors
      }

      if (mutexName) {
        if (!mutexes.has(mutexName)) {
          mutexes.set(mutexName, new Mutex())
        }

        return mutexes.get(mutexName)!.runExclusive(() => fetch(input, requestInit))
      }

      return fetch(input, requestInit)
    },
    uri: (operation) => {
      requestCount += 1

      const { domainProxy, userID: contextUserID, userSpecific } = operation.getContext()
      const graphqlEndpointURL = new URL(
        domainProxy
          ? `${window.location.protocol}//${window.location.host}/graph${graphqlEndpointDomainProxyExtra}`
          : graphqlEndpoint,
      )

      if (userSpecific) {
        const cookies = new Cookies()
        const userID = contextUserID || cookies.get('th_uid')

        if (userID) {
          graphqlEndpointURL.searchParams.set('user_id', userID)
        }
      }

      if (config.locale) {
        graphqlEndpointURL.searchParams.set('locale', config.locale)
      }

      if (config.preview) {
        graphqlEndpointURL.searchParams.set('preview_environment', 'preview')
      }

      if (config.preview && config.previewOverrideCurrentDate) {
        graphqlEndpointURL.searchParams.set('preview_override_current_date', config.previewOverrideCurrentDate)
      }

      const mutex = operationToMutexName.get(operation.operationName)
      if (mutex) {
        graphqlEndpointURL.searchParams.set('mu', mutex)
        graphqlEndpointURL.searchParams.set('muc', requestCount.toString())
      }

      return graphqlEndpointURL.toString()
    },
  }))

  const link = from(links)

  return new ApolloClient({
    cache: new InMemoryCache({
      possibleTypes: fragments.possibleTypes,
      typePolicies: {
        Block: {
          keyFields: ['type', 'id'],
        },
        Breadcrumb: {
          keyFields: ['type', 'id'],
        },
        Page: {
          keyFields: ['type', 'id'],
        },
        ProductListBlockFacetGroup: {
          keyFields: false,
        },
        ProductListBlockFacetValue: {
          keyFields: false,
        },
        ReferenceWithKey: {
          keyFields: false,
        },
        DiscountCode: {
          keyFields: false,
        },
        // Cart specific rules
        Cart: {
          fields: {
            lineItems: {
              merge: false,
            },
            totals: {
              merge: false,
            },
          },
        },
        CartLineItem: {
          fields: {
            errors: {
              merge: false,
            },
          },
        },
        CartTotal: {
          keyFields: false,
        },
      },
    }),
    link,
    ssrMode: typeof window === 'undefined',
  })
}

export const initializeApollo = (
  initialState: NormalizedCacheObject = {},
  config: Config = {},
): ApolloClient<NormalizedCacheObject> => {
  const client = apolloClient || createApolloClient(config)

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // gets hydrated here
  if (initialState) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = client.extract()

    // Merge the existing cache into data passed from getStaticProps/getServerSideProps
    const data = merge(initialState, existingCache)

    // Restore the cache with the merged data
    client.cache.restore(data)
  }

  if (typeof window === 'undefined') {
    return client
  }

  if (!apolloClient) {
    apolloClient = client
  }

  return client
}

export const initializeApolloFromContext = (
  context: GetServerSidePropsContext,
  initialState: NormalizedCacheObject = {},
) => {
  const headers: Record<string, string> = {}
  const { cookie } = context.req.headers
  if (cookie) {
    headers.cookie = cookie
  }

  forEach(context.req.headers, (value, key) => {
    if (typeof value !== 'string') {
      return
    }

    const keyLower = key.toLowerCase()
    if (!keyLower.startsWith('fastly-abtest')) {
      return
    }

    headers[keyLower] = value
  })

  return initializeApollo(
    initialState,
    {
      forwardSetCookie: (setCookie) => {
        context.res.setHeader('Set-Cookie', setCookie)
      },
      headers,
      locale: context.locale || '',
      preview: context.req.thPreview || false,
      previewOverrideCurrentDate: context.req.thPreviewOverrideCurrentDate,
    },
  )
}
